/**
 * The sidebar folder tree.
 *
 * react-arborist supplies the virtualization, keyboard model and drag-and-drop;
 * everything below is the shape of a row. Dropping a selection of files onto a
 * folder moves them, and dragging a folder onto another re-parents it - both
 * go through the same endpoints the menus use.
 */
import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { Tree, type NodeApi, type NodeRendererProps } from 'react-arborist';
import { useNavigate } from '@tanstack/react-router';
import { ChevronRight, Folder, FolderOpen, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useBulkFiles, useTree, useUpdateFolder } from '../api/hooks';
import { cx } from './ui';
import type { TreeNode } from '../api/types';

interface Node {
  id: string;
  name: string;
  ownerName: string;
  isShareRoot: boolean;
  canWrite: boolean;
  children?: Node[];
}

/** Flat rows to nested nodes, keeping the server's path ordering. */
function buildTree(nodes: TreeNode[], myId: string): Node[] {
  const byId = new Map<string, Node>();
  for (const node of nodes) {
    byId.set(node.id, {
      id: node.id,
      name: node.name,
      ownerName: node.ownerName,
      isShareRoot: node.isShareRoot,
      canWrite: node.role !== 'viewer',
      children: [],
    });
  }

  const roots: Node[] = [];
  for (const node of nodes) {
    const built = byId.get(node.id)!;
    const parent = node.parentId ? byId.get(node.parentId) : undefined;
    // A folder shared with you is a root of the tree even though it has a
    // parent, because that parent is not yours to see.
    if (parent && !node.isShareRoot) parent.children!.push(built);
    else if (node.ownerId === myId || node.isShareRoot) roots.push(built);
  }

  // A leaf with an empty children array renders as an expandable folder, so
  // strip it. Anything childless is then correctly drawn as a leaf.
  const prune = (list: Node[]): Node[] =>
    list.map((node) => {
      const children = node.children && node.children.length > 0 ? prune(node.children) : undefined;
      return { ...node, children };
    });

  return prune(roots);
}

function Row({ node, style, dragHandle }: NodeRendererProps<Node>) {
  const navigate = useNavigate();
  const open = node.isOpen && !node.isLeaf;

  return (
    <div
      ref={dragHandle}
      style={style}
      className={cx(
        'flex h-8 cursor-pointer items-center gap-1 rounded-lg pr-2 text-sm',
        node.isSelected ? 'bg-brand/15 text-ink' : 'hover:bg-surface-2',
        node.willReceiveDrop && 'ring-2 ring-brand ring-inset',
      )}
      onClick={() => {
        node.select();
        void navigate({ to: '/folders/$folderId', params: { folderId: node.id } });
      }}
    >
      <button
        type="button"
        aria-label={open ? 'Collapse' : 'Expand'}
        tabIndex={-1}
        onClick={(event) => {
          event.stopPropagation();
          node.toggle();
        }}
        className={cx(
          'flex size-5 shrink-0 items-center justify-center rounded text-ink-dim',
          node.isLeaf && 'invisible',
        )}
      >
        <ChevronRight className={cx('size-3.5 transition-transform', open && 'rotate-90')} />
      </button>

      {open ? (
        <FolderOpen className="size-4 shrink-0 text-brand" aria-hidden />
      ) : (
        <Folder className="size-4 shrink-0 text-brand" aria-hidden />
      )}

      <span className="truncate">{node.data.name}</span>

      {node.data.isShareRoot ? (
        <span
          className="ml-auto shrink-0 text-ink-dim"
          title={`Shared by ${node.data.ownerName}`}
        >
          <Users className="size-3.5" aria-hidden />
        </span>
      ) : null}
    </div>
  );
}

export function FolderTree({
  myUserId,
  activeFolderId,
  /** Files currently ticked in the main pane; dropping onto a folder moves them. */
  draggedFileIds,
}: {
  myUserId: string;
  activeFolderId: string | null;
  draggedFileIds: string[];
}) {
  const { data } = useTree();
  const updateFolder = useUpdateFolder();
  const bulkFiles = useBulkFiles();
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 240, height: 320 });

  // react-arborist needs explicit pixel dimensions, so the sidebar's own box
  // is measured rather than guessed.
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setSize({
        width: entry.contentRect.width,
        height: Math.max(120, entry.contentRect.height),
      });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const tree = useMemo(() => buildTree(data?.nodes ?? [], myUserId), [data?.nodes, myUserId]);

  const onMove = useCallback(
    async ({ dragIds, parentId }: { dragIds: string[]; parentId: string | null }) => {
      // A drag that started from the main pane's selection carries files;
      // otherwise the ids are folders from the tree itself.
      const fileIds = dragIds.filter((id) => draggedFileIds.includes(id));
      const folderIds = dragIds.filter((id) => !draggedFileIds.includes(id));

      try {
        if (fileIds.length > 0) {
          await bulkFiles.mutateAsync({ fileIds, action: 'move', folderId: parentId });
        }
        for (const id of folderIds) {
          await updateFolder.mutateAsync({ id, parentId });
        }
        toast.success('Moved');
      } catch (error) {
        toast.error('Could not move that', { description: (error as Error).message });
      }
    },
    [bulkFiles, updateFolder, draggedFileIds],
  );

  const onRename = useCallback(
    async ({ id, name }: { id: string; name: string }) => {
      try {
        await updateFolder.mutateAsync({ id, name });
      } catch (error) {
        toast.error('Could not rename', { description: (error as Error).message });
      }
    },
    [updateFolder],
  );

  const disableDrop = useCallback(
    ({ parentNode }: { parentNode: NodeApi<Node> | null }) =>
      parentNode ? !parentNode.data.canWrite : false,
    [],
  );

  if (tree.length === 0) {
    return (
      <p className="px-2 py-3 text-xs text-ink-dim">
        Folders you create or that are shared with you appear here.
      </p>
    );
  }

  return (
    <div ref={containerRef} className="min-h-0 flex-1">
      <Tree<Node>
        data={tree}
        width={size.width}
        height={size.height}
        indent={14}
        rowHeight={32}
        openByDefault={false}
        selection={activeFolderId ?? undefined}
        onMove={onMove}
        onRename={onRename}
        disableDrop={disableDrop}
        disableEdit={(node) => !node.canWrite}
      >
        {Row}
      </Tree>
    </div>
  );
}
